import {
  BehaviorPersonaEnum,
  LifestylePersonaEnum,
  RoommatePersonaEnum,
} from "@/__generated__/types";
import {
  Sparkles,
  BoomBox,
  HeartHandshake,
  Moon,
  Scale,
  Brain,
  Megaphone,
  Palette,
  PocketKnife,
  Handshake,
  Target,
  Heart,
  Mic,
  Rocket,
  Ruler,
  Flower2,
  Briefcase,
  Sofa,
  Globe,
  BookOpen,
  MoonStar,
  Feather,
  Mountain,
  Binoculars,
  Wind,
  LucideIcon,
} from "lucide-react";

export type PersonaType = "lifestyle" | "behavior" | "roommate";

export interface PersonaData {
  title: string;
  description: string;
  communicationTips: string[];
  icon: LucideIcon;
}

export const ROOMMATE_PERSONAS: Record<RoommatePersonaEnum, PersonaData> = {
  [RoommatePersonaEnum.TidySocializer]: {
    title: "Tidy Socializer",
    description:
      "These are your gold-standard roomies — they'll greet you with a smile and a Lysol wipe. They host lowkey hangouts, label their food containers, and somehow wake up for 8 AM lectures without rage. Think: study dates, fairy lights, and good boundaries. They're the glue of the friend group and the reason the apartment smells like eucalyptus.",
    communicationTips: [
      "Keep messages organized and concise.",
      "Use friendly, structured language.",
      "Show appreciation for balance (clean + social).",
      "Avoid chaotic or overly casual communication.",
    ],
    icon: Sparkles,
  },
  [RoommatePersonaEnum.SocialTrailblazer]: {
    title: "Social Trailblazer",
    description:
      'This is the squad with the open door, loud music, and "Hey, you\'re welcome to join!" energy. You\'ll find them dancing at 1 AM, throwing impromptu game nights, or hosting mystery Taco Tuesdays. Clean-ish, but who\'s keeping score? If your love language is chaos and community, this is your crowd.',
    communicationTips: [
      "Match their energy with enthusiasm.",
      "Use expressive, upbeat wording.",
      "Group-style interactions work well.",
      "Avoid long delays or low-energy messages.",
    ],
    icon: BoomBox,
  },
  [RoommatePersonaEnum.RoommateSoulmate]: {
    title: "Roommate Soulmate",
    description:
      "They're not just looking for a roommate — they want a bond. This group is here for the shared playlists, comfort talks, and joint skincare routines. You'll be tagged in memes, included in every group plan, and asked how your day really was. Rooming with them feels less like cohabiting and more like joining a mini family.",
    communicationTips: [
      "Use warm, personal, relational language.",
      "Acknowledge feelings and social compatibility.",
      "Adopt a gentle, conversational tone.",
      "Avoid overly transactional or robotic messages.",
    ],
    icon: HeartHandshake,
  },
  [RoommatePersonaEnum.ChillIntrovert]: {
    title: "Chill Introvert",
    description:
      "This crew keeps it quiet, cozy, and perfectly low-key. Think soft blankets, 10-hour lo-fi beats, and a rotating queue of Netflix dramas. They're kind, respectful, and will always Venmo you their share — just don't expect them to party or host. They disappear into their rooms and emerge only for snacks and small talk.",
    communicationTips: [
      "Keep things low-pressure.",
      "Use calmer, shorter, softer phrasing.",
      "Provide options instead of expectations.",
      "Avoid over-enthusiasm or pushy follow-ups.",
    ],
    icon: Moon,
  },
  [RoommatePersonaEnum.BalancedBuddy]: {
    title: "Balanced Buddy",
    description:
      'This is the default hero of the roommateverse — friendly, balanced, and basically everyone\'s ideal match. They\'re the ones who bring snacks to group study, clean their half of the room, and always say "hey" in the hallway. Not too wild, not too strict — just a chill, dependable presence with great vibes and decent snacks.',
    communicationTips: [
      "Keep the tone friendly and flexible.",
      "Present balanced choices and middle-ground options.",
      "Use neutral energy - not too high, not too flat.",
      "Avoid extremes (overly formal or overly casual).",
    ],
    icon: Scale,
  },
};

export const BEHAVIOR_PERSONAS: Record<BehaviorPersonaEnum, PersonaData> = {
  [BehaviorPersonaEnum.ThoughtfulStrategist]: {
    title: "Thoughtful Strategist",
    description:
      'Calm, calculated, and always thinking two moves ahead. They\'re the "let\'s spreadsheet this" kind of people who bring balance to chaos. They recharge alone and deliver brilliance quietly.',
    communicationTips: [
      "Be clear, logical, and structured.",
      "Give context before asking something.",
      "Offer step-by-step or side-by-side comparisons.",
      "Avoid emotional appeals without facts.",
    ],
    icon: Brain,
  },
  [BehaviorPersonaEnum.ExpressiveExtrovert]: {
    title: "Expressive Extrovert",
    description:
      "The life of every room they walk into. They fuel on social energy and memes. If there's a group chat, they run it. Natural communicators, leaders, and chaos coordinators.",
    communicationTips: [
      "Use emojis, energy, and expressive tone.",
      "Encourage them to share their thoughts.",
      "Make participation feel fun.",
      "Avoid dry, overly formal communication.",
    ],
    icon: Megaphone,
  },
  [BehaviorPersonaEnum.ArtisticIdealist]: {
    title: "Artistic Idealist",
    description:
      "Big emotions, big ideas. They doodle in meetings and write poetry on their phones. They believe art can heal the world — and they're probably halfway through a passion project right now.",
    communicationTips: [
      "Use emotionally aware, expressive language.",
      "Mention values, purpose, and creative space.",
      "Invite them to share their work or ideas.",
      "Avoid purely transactional or clinical messaging.",
    ],
    icon: Palette,
  },
  [BehaviorPersonaEnum.SwissArmyKnife]: {
    title: "Swiss Army Knife",
    description:
      "They're good at everything. The friend who's a designer, coder, musician, and somehow also runs marathons. Their mind is a mixtape — always skipping genres but never off-beat.",
    communicationTips: [
      "Recognize their range of skills.",
      "Offer options, not obligations.",
      "Keep messaging flexible and open-ended.",
      "Avoid pigeonholing them into one narrow lane.",
    ],
    icon: PocketKnife,
  },
  [BehaviorPersonaEnum.DependableHarmonizer]: {
    title: "Dependable Harmonizer",
    description:
      'Team glue. They make collaboration feel effortless, thrive on order, and keep everyone on track. When they say, "I\'ll take care of it," they mean it — and they deliver early.',
    communicationTips: [
      "Emphasize community, belonging, and harmony.",
      "Use warm, appreciative language.",
      "Make it clear others will value their presence.",
      "Avoid competitive, conflict-heavy framing.",
    ],
    icon: Handshake,
  },
  [BehaviorPersonaEnum.GroundedGoGetter]: {
    title: "Grounded Go-Getter",
    description:
      'Down-to-earth and sensible, these are your "voice of reason" friends. They like results, not noise. Reliable, organized, and sometimes low-key hilarious without trying.',
    communicationTips: [
      "Be grounded, honest, and opportunity-focused.",
      "Show how something pays off in reality.",
      "Acknowledge their time and priorities.",
      'Avoid exaggeration or vague "life-changing" claims.',
    ],
    icon: Target,
  },
  [BehaviorPersonaEnum.EmpatheticAnalyst]: {
    title: "Empathetic Analyst",
    description:
      "Equal parts compassion and logic. They understand people and data. They're the ones you want in charge when empathy and decision-making need to coexist.",
    communicationTips: [
      "Combine emotional awareness with clear reasoning.",
      "Mention people, impact, and systems together.",
      "Invite them into roles where listening and analysis matter.",
      "Avoid messages that are all logic or all emotion with no balance.",
    ],
    icon: Heart,
  },
  [BehaviorPersonaEnum.RadiantStoryteller]: {
    title: "Radiant Storyteller",
    description:
      "They light up rooms. Artists, performers, idea-spreaders — everything is an expression. They're powered by connection and creativity. Expect them to have five passion projects and a killer playlist.",
    communicationTips: [
      "Use vivid, lively language.",
      "Invite them to share, host, or present.",
      "Show that their presence can brighten spaces.",
      "Avoid flat, purely informational messages.",
    ],
    icon: Mic,
  },
  [BehaviorPersonaEnum.InspiredMaverick]: {
    title: "Inspired Maverick",
    description:
      "Pure creative fire. Emotionally charged, deeply authentic, and impossible to ignore. They're not following trends — they're making them. The art-school philosopher energy in human form.",
    communicationTips: [
      "Affirm their uniqueness and intensity.",
      "Keep invitations open and flexible.",
      "Highlight spaces that tolerate risk and experimentation.",
      "Avoid rigid, rule-heavy or judgy tones.",
    ],
    icon: Rocket,
  },
  [BehaviorPersonaEnum.PrecisionBuilder]: {
    title: "Precision Builder",
    description:
      "They see systems where others see chaos. Reliable, technical, and process-driven, they thrive where structure matters. Engineers, analysts, or anyone who secretly loves a good to-do list.",
    communicationTips: [
      "Use precise language and concrete details.",
      "Provide timelines, processes, and clear expectations.",
      "Show how systems fit together.",
      "Avoid vague, hand-wavy, or inconsistent info.",
    ],
    icon: Ruler,
  },
};

export const LIFESTYLE_PERSONAS: Record<LifestylePersonaEnum, PersonaData> = {
  [LifestylePersonaEnum.Changemaker]: {
    title: "Changemaker",
    description:
      "The go-getters who want to build startups that make the world better. They're the ones who'll launch eco-conscious apps, quote Malala and Elon in the same breath, and still find time to hit a yoga class. They wear purpose like a brand.",
    communicationTips: [
      "Highlight impact and problem-solving.",
      "Use motivational, forward-focused language.",
      "Give opportunities to lead or join meaningful projects.",
      "Avoid passive or generic messaging.",
    ],
    icon: Flower2,
  },
  [LifestylePersonaEnum.HustleArchitect]: {
    title: "Hustle Architect",
    description:
      "These folks live for the next opportunity. They love structure, ambition, and practical success. They're the friends who have a color-coded Notion dashboard, drink black coffee, and are three steps ahead of you in every plan.",
    communicationTips: [
      "Acknowledge their drive and ambition.",
      "Be efficient and high-energy.",
      "Offer opportunities that help them grow or network.",
      "Avoid slow-paced or overly casual messages.",
    ],
    icon: Briefcase,
  },
  [LifestylePersonaEnum.ChillRealist]: {
    title: "Chill Realist",
    description:
      "Laid-back but reliable. They're not chasing fame or fortune — they're about stability and good vibes. Probably the ones who plan the group trip, remember everyone's birthday, and keep the chaos under control.",
    communicationTips: [
      "Keep it real, calm, and simple.",
      "Use relaxed wording and straightforward phrasing.",
      "Reinforce low-pressure, low-drama vibes.",
      "Avoid hype or overly ambitious language.",
    ],
    icon: Sofa,
  },
  [LifestylePersonaEnum.CultureCurator]: {
    title: "Culture Curator",
    description:
      "They spot trends before they're trends — the friend who tells you about a band six months before it's on TikTok. Socially aware but stylish, they believe being fashionable and being kind aren't mutually exclusive.",
    communicationTips: [
      "Ask about interests, taste, or trends.",
      "Use slightly polished or aesthetic language.",
      'Share "cool" opportunities or interesting groups.',
      "Avoid generic or outdated references.",
    ],
    icon: Globe,
  },
  [LifestylePersonaEnum.GroundedDisciple]: {
    title: "Grounded Disciple",
    description:
      "They live by a code — faith, fitness, and focus. Early risers, protein shakes, Sunday service. They've got discipline down to an art and inspire others to balance body and spirit.",
    communicationTips: [
      "Be respectful, steady, and intentional.",
      "Acknowledge values, routines, or commitments.",
      "Keep communication meaningful and not overly casual.",
      "Avoid chaotic or inconsistent messaging.",
    ],
    icon: BookOpen,
  },
  [LifestylePersonaEnum.AestheticDreamer]: {
    title: "Aesthetic Dreamer",
    description:
      "They care deeply — about beauty, people, and purpose. They'll spend hours making a Canva post about climate change that actually looks good. Creative souls who believe aesthetics can drive change.",
    communicationTips: [
      "Use warm, expressive, slightly poetic phrases.",
      "Show appreciation for creativity and beauty.",
      "Provide visually pleasing or emotionally resonant content.",
      "Avoid dry or overly direct communication.",
    ],
    icon: MoonStar,
  },
  [LifestylePersonaEnum.SimpleSage]: {
    title: "Simple Sage",
    description:
      "Less is more. They're the minimalist travelers who own one good jacket, one reusable bottle, and a thousand stories. They prioritize calm, clarity, and causes that matter.",
    communicationTips: [
      "Use minimal words, clean formatting, and clarity.",
      "Provide only what's needed - no clutter.",
      "Respect that less is more.",
      "Avoid long messages or complicated asks.",
    ],
    icon: Feather,
  },
  [LifestylePersonaEnum.ZenHustler]: {
    title: "Zen Hustler",
    description:
      'They meditate before meetings. They\'re into kombucha, climate action, and career growth — a balanced cocktail of mindfulness and motivation. They\'re future CEOs who say "let\'s take a deep breath" unironically.',
    communicationTips: [
      "Balance calm reassurance with purposeful energy.",
      "Mention well-being alongside achievement.",
      "Keep structure, but avoid urgency.",
      "Avoid extremes (over-relaxed or overly intense).",
    ],
    icon: Mountain,
  },
  [LifestylePersonaEnum.QuietObserver]: {
    title: "Quiet Observer",
    description:
      "Reserved, skeptical, or just busy with real life. They scroll, not post. They value routine, family, and familiarity, not flashy trends. When they engage, it's genuine — never performative.",
    communicationTips: [
      "Be gentle, patient, and low-pressure.",
      "Offer space to respond when they choose.",
      "Avoid spotlighting them or requiring fast engagement.",
      "Avoid loud, busy, or overly expressive communication.",
    ],
    icon: Binoculars,
  },
  [LifestylePersonaEnum.KeeperOfCalm]: {
    title: "Keeper of Calm",
    description:
      "Grounded, faithful, and quietly consistent. They're not loud online, but they're the ones everyone trusts offline. They believe in simple joys and steady values in a noisy world.",
    communicationTips: [
      "Use steady, grounding, consistent tone.",
      "Provide predictable communication patterns.",
      "Emphasize peace, stability, and comfort.",
      "Avoid chaotic or overly emotional messaging.",
    ],
    icon: Wind,
  },
};

export const PERSONA_COLORS = {
  lifestyle: {
    gradient: "from-purple-100 to-purple-50 dark:from-purple-900 dark:to-purple-950",
    border: "border-purple-100 dark:border-purple-800",
    text: "text-purple-900 dark:text-purple-200",
    label: "text-purple-900 dark:text-purple-200",
    iconBg: "bg-purple-100 dark:bg-purple-900",
  },
  behavior: {
    gradient: "from-orange-100 to-orange-50 dark:from-orange-900 dark:to-orange-950",
    border: "border-orange-100 dark:border-orange-800",
    text: "text-orange-700 dark:text-orange-200",
    label: "text-orange-700 dark:text-orange-200",
    iconBg: "bg-orange-100 dark:bg-orange-900",
  },
  roommate: {
    gradient: "from-green-100 to-green-50 dark:from-green-900 dark:to-green-950",
    border: "border-green-100 dark:border-green-800",
    text: "text-green-700 dark:text-green-200",
    label: "text-green-700 dark:text-green-200",
    iconBg: "bg-green-100 dark:bg-green-900",
  },
} as const;
